/**
 * task-209 / D2-470: helpers for deciding how basket & order lines render in
 * the summaries.
 *
 * A cart-level promo code makes the backend add a synthetic `promotion_item`
 * line to `basket.items` (flagged `isPromoCode: true`). That line has a
 * negative price, no image and a dead product link, so it must never appear in
 * a product-line list, nor be counted in the basket item total — exactly like
 * the existing minimum-order-value adjustment and material-surcharge lines.
 * The cart-level discount is surfaced separately as a summary "discount" row
 * from `basketDiscountPrice` / `order.discount`.
 *
 * Kept as a pure CommonJS module (matching `formSubmitExclusions.js`) so the
 * predicates can be unit-tested with `node:test` without a React renderer:
 *
 *     node --test src/services/basketLineDisplay.test.js
 */

/**
 * True when a basket/order line is the synthetic promo-code (`promotion_item`)
 * line the backend adds for a cart-level promo code.
 *
 * @param {object | null | undefined} item a basket or order line item
 * @returns {boolean}
 */
function isPromoCodeLine(item) {
  return Boolean(item && item.isPromoCode === true)
}

/**
 * @typedef {Object} Money
 * @property {number|string} amount
 * @property {string} [currency]
 */

/**
 * Coerce a Money amount (which the backend serialises as either a number or a
 * decimal string) to a finite number, or null when it is absent / unparseable.
 *
 * Every display gate and resolver in this module shares this one parse, and
 * keeping it here means widgets/displayPrice.js and the checkout components can
 * reach it without a shared widget - or one feature folder - depending on
 * another's internals.
 *
 * @param {number|string|null|undefined} value
 * @returns {number|null}
 */
function toAmount(value) {
  // Money is a number or a decimal string and nothing else. These guards run
  // before the coercion below because Number('') and Number([]) are both 0, so
  // an absent amount would otherwise read as a genuine zero saving.
  if (typeof value !== 'number' && typeof value !== 'string') {
    return null
  }
  if (typeof value === 'string' && value.trim() === '') {
    return null
  }

  // Number rather than parseFloat: parseFloat keeps whatever prefix it could
  // read and discards the rest, so '1,234.50' parsed to 1. Every gate and
  // renderer here shares this one parse, so both agreed on that wrong number
  // and nothing downstream could catch it. Number rejects the whole value.
  //
  // isFinite rather than isNaN: Number.isNaN(Infinity) is false, so '1e999'
  // reached the renderer and formatted as the infinity glyph.
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Resolve the amount to render for a cart-level promotional discount.
 *
 * `showPositiveAmount` takes the magnitude rather than negating. The two only
 * differ when EP sends a *positive* amount, and that has never been observed:
 * every discount visible on the wire is negative - 118 of 118 events across 9
 * distinct values, measured 2026-09-15 from the BasketDeliveryLineKeptForPromo
 * breadcrumb. (Production logs no promo telemetry at all, so that is the test
 * environment.)
 *
 * ⚠️ Scope, because this was got wrong once: that measurement - and the
 * WebstoreBackEnd comment usually quoted alongside it (task-281,
 * ElasticPathBasketRepository.LineCarriesDiscount, "a saving can present as
 * -5.00 or 5.00 depending on context ... test the magnitude, never a signed
 * comparison") - both concern EP's *per-line* discount. This function reads the
 * *cart-level* Meta.DisplayPrices.Discount, which nothing logs. Strong evidence
 * for taking the magnitude here; not proof.
 *
 * An earlier revision negated instead, so that a positive amount would fail the
 * gate rather than label a charge a saving. That is the worse failure: it loses
 * the customer's saving line on every surface at once while the subtotal stays
 * discounted. If a positive amount ever does arrive it should be logged, not
 * silently resolved one way or the other.
 *
 * Zero is a value, not an absence: a zero discount returns 0. Do not collapse
 * the `amount === null` check into `if (!amount)`.
 *
 * @param {{ amount?: number|string|null } | null | undefined} discount
 * @param {boolean} [showPositiveAmount]
 * @returns {number|null}
 */
function resolveDiscountAmount(discount, showPositiveAmount = false) {
  if (!discount) {
    return null
  }

  const amount = toAmount(discount.amount)
  if (amount === null) {
    return null
  }

  // Math.abs also normalises -0, which would otherwise format as "-0.00".
  return showPositiveAmount ? Math.abs(amount) : amount
}

/**
 * True when a basket carries a promotional saving worth showing a row for.
 *
 * Gates the rows that D2-536/D2-537 relabel "Total Saving", on the same
 * resolution of the amount those rows render with, so the gate and the renderer
 * cannot disagree. Magnitude, not sign, for the reason given above: a
 * positive-signed amount is still a saving, and must still show a row.
 *
 * @param {{ amount?: number|string|null } | null | undefined} discount
 * @returns {boolean}
 */
function hasPromoSaving(discount) {
  const saving = resolveDiscountAmount(discount, true)

  // Rounded to the pennies the row actually renders - formatDecimalString is
  // 2dp - so a sub-penny saving cannot put a "Total Saving" label against
  // "£0.00". Gating on the raw amount let 0.004 through, which is reachable
  // from a percentage-off promotion on a low-value line.
  return saving !== null && Math.round(saving * 100) !== 0
}

/**
 * @typedef {Object} LineDiscountDisplay
 * @property {number} originalAmount  Magnitude of the pre-discount price.
 * @property {string|null} currency   ISO currency carried through from the price.
 */

/**
 * Resolve the struck-through "was" price for a basket/checkout/order line
 * (task-213, D2-467).
 *
 * Promo codes reach the FE as a per-line discount; task-208 adds the
 * pre-discount ("was") price to each basket line as `originalUnitPrice` /
 * `originalTotalPrice` (Money objects). When EP gives no pre-discount price
 * task-208 falls the original back to the current price, so an undiscounted
 * line has `original === current`.
 *
 * This is the single decision "does this line have a real discount worth
 * striking through the old price for?", so the three render sites - the basket
 * line, the checkout basket summary and the order-confirmation summary - cannot
 * disagree. D2-536 also colours the line total on a discounted line, so each
 * render site resolves this once and drives both halves from the result.
 *
 * Returns `null` - nothing renders and the line is unchanged - when:
 *   - either price object is missing (defensive: task-208 not yet deployed to
 *     the target env, so the original-price field is simply absent);
 *   - either amount is unparseable;
 *   - the original is not strictly greater than the current price (no discount,
 *     or task-208's fallback made them equal).
 *
 * @param {Money|null|undefined} originalPrice  The pre-discount ("was") price.
 * @param {Money|null|undefined} currentPrice   The current (discounted) price.
 * @returns {LineDiscountDisplay|null}
 */
function resolveLineDiscount(originalPrice, currentPrice) {
  if (!originalPrice || !currentPrice) {
    return null
  }

  const original = toAmount(originalPrice.amount)
  const current = toAmount(currentPrice.amount)

  if (original === null || current === null) {
    return null
  }

  // A real discount only exists when the "was" price is strictly higher than
  // what is charged now. Equal amounts are task-208's undiscounted fallback.
  if (original <= current) {
    return null
  }

  return {
    originalAmount: original,
    currency: originalPrice.currency || currentPrice.currency || null,
  }
}

/**
 * True when a basket/order line is a real product line — i.e. not one of the
 * synthetic/derived lines (promo code, delivery charge, minimum-order-value
 * adjustment, material surcharge) the backend appends. This is the single
 * shared "is this a product?" predicate: the checkout gate, the header/badge
 * count (`countBasketItems`) and any summary that wants a product-only view
 * must agree on it rather than each re-deriving a subtly different filter.
 *
 * Null-tolerant like `isPromoCodeLine` — a `null`/`undefined` item is not a
 * product line and must never throw (task-330 #7).
 *
 * @param {object | null | undefined} item a basket or order line item
 * @returns {boolean}
 */
function isProductLine(item) {
  return Boolean(
    item &&
    isPromoCodeLine(item) === false &&
    item.isDeliveryCharge !== true &&
    item.isMinimumOrderValueAdjustment !== true &&
    item.isMaterialSurcharge !== true
  )
}

/**
 * Finds the synthetic minimum-order-value adjustment LINE on a cart. Used by
 * the checkout summary and both order summaries, where that line is REAL -
 * it is on the cart/order and is what the customer is charged, so the line
 * is authoritative there.
 *
 * ⚠️ The BASKET page's MOV row deliberately does NOT use this: pre-checkout
 * the cart carries no MOV line (any found is stale checkout state the
 * backend strips), so that row renders the estimate response's
 * minimumOrderValueAdjustment - the rule-computed value inside the estimated
 * total. Two sources by design, split by lifecycle: rule value before
 * checkout, cart line from checkout onwards (task-529 review round 3).
 *
 * @param {Array | null | undefined} basketItems
 * @returns {object | null}
 */
function findMinimumOrderValueAdjustmentLine(basketItems) {
  if (!Array.isArray(basketItems)) {
    return null
  }
  return basketItems.find((item) => item && item.isMinimumOrderValueAdjustment === true) || null
}

/**
 * True when `basketItems` contains no real product line — even if derived
 * lines (an applied promo code, a promo-discounted delivery line, …) remain.
 * A promo-only basket still has `basketItems.length >= 1`, so a bare
 * length/empty check is not enough for the checkout gate.
 *
 * Guarded with `Array.isArray` so a not-yet-loaded (`null`) or malformed
 * (non-array) value returns `true` rather than throwing (task-330 #7);
 * distinguishing "not loaded" from "loaded but empty" is the caller's job —
 * see `decideCheckoutGate`.
 *
 * @param {Array | null | undefined} basketItems
 * @returns {boolean}
 */
function hasNoProductLines(basketItems) {
  if (!Array.isArray(basketItems)) {
    return true
  }
  return !basketItems.some(isProductLine)
}

/**
 * Pure decision for the checkout gate (task-330 #1/#3/#4, task-339 §1). Kept
 * here, out of the React component, so it can be exhaustively unit-tested
 * without a renderer; `protectCheckout` is a thin shell that maps the result to
 * render/navigate.
 *
 *  - `'redirect'` — no products (incl. promo-only), the load failed, OR the
 *                   basket is not yet loaded: send the user back to the basket
 *                   page (self-healing, never a dead end).
 *  - `'show'`     — real products present: render the checkout content.
 *
 * There is deliberately no `'wait'` outcome (task-339). Checkout parity: a
 * hard entry (load / refresh / bookmark of /checkout) arrives before the basket
 * has resolved, so `basketItemsLoaded !== true`, and it bounces back to the
 * basket — the original product contract, self-healing and never reported as a
 * bug. Client-side checkout entries must originate from a surface where
 * `SET_BASKET` has run — today `basketButtons.js` (the basket page),
 * `checkoutPaymentFailedPage.js` (CheckoutLayout, its own whoAmI runs getBasket),
 * and the `basketPage.js` Login & Checkout resume (D2-487, gated by
 * `decideCheckoutResume` below, which only navigates once this gate says
 * `'show'`); all arrive with `basketItemsLoaded === true`, so they `'show'`
 * instantly.
 * A future entry point that navigates before the basket loads will silently
 * bounce to the basket page (by design: parity). This invariant is enforced by
 * call-site discipline (a note lives at each of those two sites) — a property
 * test is not practicable without a renderer. Removing the wait outcome removes
 * the fetch dependency that a not-yet-settled whoAmI singleton could eat, so
 * nothing can hang the gate on a spinner.
 *
 * @param {{ basketItemsLoaded?: boolean, basketItemsLoadFailed?: boolean, basketItems?: Array | null } | null | undefined} basket
 * @returns {'redirect' | 'show'}
 */
function decideCheckoutGate(arg) {
  // Null-tolerant to match this module's convention: a `= {}` default only covers
  // `undefined`, so an explicit `null` argument would throw on destructuring.
  const { basketItemsLoaded, basketItemsLoadFailed, basketItems } = arg || {}
  if (basketItemsLoadFailed === true) {
    return 'redirect'
  }
  if (basketItemsLoaded !== true) {
    return 'redirect'
  }
  return hasNoProductLines(basketItems) === true ? 'redirect' : 'show'
}

/**
 * Pure decision for the guest Login & Checkout resume (D2-487, IMI answer
 * 2026-09-24). The basket panel's Login & Checkout button sends a guest through
 * Auth0 and back to the BASKET page carrying `?resume-checkout=1` — never to
 * /checkout directly, because a hard checkout entry always bounces back to the
 * basket (decideCheckoutGate above, task-339 parity). The basket page then
 * finishes the journey with the sanctioned CLIENT-SIDE entry into checkout,
 * driven by this decision; `basketPage.js` is the thin shell.
 *
 *  - `'wait'`     — the marker is present but identity or the basket has not
 *                   resolved yet: do nothing, re-evaluate on the next context
 *                   change. Both resolutions are guaranteed to arrive (whoAmI
 *                   settles queryingUser on every path, and the basket load
 *                   settles loaded/failed), so 'wait' cannot be terminal.
 *  - `'navigate'` — resolved, signed in, allowed to check out, gate shows:
 *                   enter checkout now.
 *  - `'stay'`     — terminal no: no marker, not signed in after all, a
 *                   punchout session (their sanctioned exit is the punchout
 *                   return, never /checkout — same ranking as the summary's
 *                   cta discriminator), the account cannot check out (fresh
 *                   registrants without an approved trade account land here
 *                   by design), or the basket finished loading
 *                   productless/failed. The shell strips the marker so
 *                   back-navigation cannot re-trigger the resume.
 *
 * @param {{ requested?: boolean, queryingUser?: boolean, email?: string | null,
 *           canCheckout?: boolean, isPunchout?: boolean,
 *           basket?: { basketItemsLoaded?: boolean, basketItemsLoadFailed?: boolean, basketItems?: Array | null } | null
 *         } | null | undefined} arg
 * @returns {'wait' | 'navigate' | 'stay'}
 */
function decideCheckoutResume(arg) {
  const { requested, queryingUser, email, canCheckout, isPunchout, basket } = arg || {}
  if (requested !== true) {
    return 'stay'
  }
  if (queryingUser !== false) {
    return 'wait'
  }
  if (!email || canCheckout !== true || isPunchout === true) {
    return 'stay'
  }
  if (decideCheckoutGate(basket) === 'show') {
    return 'navigate'
  }
  // The gate says redirect. On a SETTLED basket that is final - productless
  // or failed load - and we are already on the basket page, so stay. Before
  // the load settles it may still become 'show'.
  if (basketLoadSettled(basket) === true) {
    return 'stay'
  }
  return 'wait'
}

/**
 * Whether the basket load has reached a terminal state (successfully loaded or
 * failed). Kept next to decideCheckoutGate so the knowledge of WHICH context
 * fields represent settledness lives in exactly one place — the gate folds
 * "not settled" into 'redirect' by design (task-339: no wait state), so
 * callers that DO need the distinction (decideCheckoutResume) read it from
 * here rather than re-deriving the fields.
 *
 * @param {{ basketItemsLoaded?: boolean, basketItemsLoadFailed?: boolean } | null | undefined} basket
 * @returns {boolean}
 */
function basketLoadSettled(basket) {
  const { basketItemsLoaded, basketItemsLoadFailed } = basket || {}
  return basketItemsLoaded === true || basketItemsLoadFailed === true
}

/**
 * One decision for the basket-page summary's estimate rendering (task-529,
 * review rounds 1-4): the delivery row, the MOV row, the subtotal swap, the
 * total swap and the "including/excluding delivery" caption all derive from
 * this one result, and the three money swaps move AS ONE - either the panel
 * renders the backend's subtotal + delivery + MOV == total set complete, or
 * none of it. A total without its subtotal (version skew, a proxy dropping a
 * field) would render a column that visibly does not add, which is worse
 * than the legacy rendering it degrades to.
 *
 * The estimate is refused WHOLESALE (legacy rendering, all outputs false)
 * whenever mixing it in could put contradictory or unrenderable money on
 * screen:
 *   - a readonly basket with a real shipping row (its total is built on a
 *     different delivery basis);
 *   - no renderable currency (DisplayPriceAsString renders nothing for a
 *     falsy code - the legacy renderer degrades better);
 *   - an estimate currency that contradicts the panel currency (the backend
 *     summed them as one unit; showing $ above a GBP total would lie);
 *   - any of subtotal/total missing or unparseable (the swaps move as one).
 *
 * Within a usable estimate: the delivery row needs a usable estimate amount
 * (the total may swap without one - no default address - and the caption
 * then stays "excluding delivery"); the MOV row needs a non-zero adjustment.
 *
 * task-547 (D2-488 phase 2) pulls the material surcharge OUT of the backend's
 * subtotal/total and into its own `materialSurchargeEstimate` field (PR #1935:
 * `baseWithoutTax` now subtracts the surcharge line and the estimate is
 * recomputed separately), reversing phase 1's "surcharge stays inside, no gate
 * needed" note above - that was true of `subtotalWithoutTax` ON THE OLD
 * BACKEND SHAPE only. `useEstimatedSurcharge` decides the SOURCE (estimate vs
 * the browsing basket's stale surcharge line), independently of whether the
 * amount is worth a row - and deploy-order safety CUTS THE OTHER WAY (review
 * round 1): when an old backend sends usable figures with
 * `materialSurchargeEstimate` simply absent, its `subtotalWithoutTax` still
 * CONTAINS the surcharge (phase 1's documented shape), so the legacy
 * line-based row must stay HIDDEN there too or the column double-displays
 * it. `showLegacySurchargeRow` is therefore true ONLY in the legacy-figures
 * state (figuresUsable false, line present); whenever figures are usable,
 * exactly one surcharge representation is on screen - the estimated row, or
 * none. `showSurchargeRow` gates the estimated row on non-zero, exactly like
 * the MOV row - a truthful zero surcharge is not worth a line.
 *
 * `showTaxRow` follows the SAME figuresUsable gate but, unlike MOV/surcharge,
 * is NOT gated on non-zero: checkout's own Tax row (`basketSummary.js`
 * `BasketTaxTotal`) renders unconditionally once a country resolves, and a
 * truthful zero (tax-exempt country, distributor account) is real information
 * worth a "£0.00" line, not a suppressed row. `taxEstimate` null ("not
 * computed" - no resolvable tax country pre-checkout, or a failed rate read)
 * hides the row entirely; it is never rendered as 0.00.
 *
 * `useTaxInclusiveTotal` decided (Dean, 2026-09-23): once the backend supplies
 * `estimatedTotalWithTax` (= ex-tax total + tax, computed backend-side - null
 * exactly when tax itself is null), the Total row renders that tax-inclusive
 * figure instead of `estimatedTotalWithoutTax`, so the visible column sums
 * exactly as checkout's own tax-inclusive Total does. Absent/null falls back
 * to the ex-tax total with its existing caption - never invented on the FE.
 *
 * @param {object} args
 * @param {boolean | null | undefined} args.isReadonly
 * @param {boolean} args.hasRealDeliveryCharge
 * @param {string | null | undefined} args.summaryCurrency the currency every swapped row renders in
 * @param {{ amount: number | string | null, currency?: string | null } | null | undefined} args.deliveryEstimate
 * @param {number | string | null | undefined} args.estimatedTotalWithoutTax
 * @param {number | string | null | undefined} args.subtotalWithoutTax
 * @param {number | string | null | undefined} args.minimumOrderValueAdjustment
 * @param {number | string | null | undefined} args.materialSurchargeEstimate
 * @param {number | string | null | undefined} args.taxEstimate
 * @param {number | string | null | undefined} args.estimatedTotalWithTax
 * @param {boolean | null | undefined} args.hasLegacySurchargeLine the browsing basket carries a surcharge LINE
 * @returns {{ hasShippingRow: boolean, showDeliveryEstimateRow: boolean, useEstimatedTotal: boolean, useEstimatedSubtotal: boolean, showMovRow: boolean, totalIncludesDelivery: boolean, useEstimatedSurcharge: boolean, showSurchargeRow: boolean, showLegacySurchargeRow: boolean, showTaxRow: boolean, useTaxInclusiveTotal: boolean }}
 */
function decideSummaryDelivery({
  isReadonly,
  hasRealDeliveryCharge,
  summaryCurrency,
  deliveryEstimate,
  estimatedTotalWithoutTax,
  subtotalWithoutTax,
  minimumOrderValueAdjustment,
  materialSurchargeEstimate,
  taxEstimate,
  estimatedTotalWithTax,
  hasLegacySurchargeLine,
}) {
  const hasShippingRow = isReadonly === true && hasRealDeliveryCharge === true
  const estimateCurrency = deliveryEstimate ? deliveryEstimate.currency : null
  const currencyMismatch = Boolean(
    estimateCurrency && summaryCurrency && estimateCurrency !== summaryCurrency
  )
  const figuresUsable =
    hasShippingRow === false &&
    Boolean(summaryCurrency) &&
    currencyMismatch === false &&
    toAmount(estimatedTotalWithoutTax) !== null &&
    toAmount(subtotalWithoutTax) !== null
  const estimateUsable =
    deliveryEstimate !== null &&
    deliveryEstimate !== undefined &&
    toAmount(deliveryEstimate.amount) !== null
  const showDeliveryEstimateRow = estimateUsable && figuresUsable
  const movAmount = toAmount(minimumOrderValueAdjustment)
  const showMovRow = figuresUsable && movAmount !== null && Math.round(movAmount * 100) !== 0
  const surchargeAmount = toAmount(materialSurchargeEstimate)
  const useEstimatedSurcharge = figuresUsable && surchargeAmount !== null
  const showSurchargeRow =
    useEstimatedSurcharge && Math.round(surchargeAmount * 100) !== 0
  const taxAmount = toAmount(taxEstimate)
  const showTaxRow = figuresUsable && taxAmount !== null
  const taxInclusiveTotalAmount = toAmount(estimatedTotalWithTax)
  // Review round 3: gated on showTaxRow too - with drift the OTHER way (total-with-tax present,
  // taxEstimate null) the Total must not render a tax-inclusive figure above a column with no Tax
  // row to explain it. The backend nulls them together, but no row here trusts two fields to agree.
  const useTaxInclusiveTotal = showTaxRow && taxInclusiveTotalAmount !== null
  // Review round 1 (2): the browsing-basket surcharge LINE only earns a row in the LEGACY-figures
  // state. Whenever the estimate's figures are usable, the surcharge money is already accounted
  // for - either as materialSurchargeEstimate (own row) or, on a phase-1 backend that has not
  // pulled it out yet, folded inside subtotalWithoutTax - so rendering the line as an extra row
  // in the FE-ahead-of-BE window would double-display it and break the visible column sum.
  const showLegacySurchargeRow =
    figuresUsable === false && hasLegacySurchargeLine === true
  return {
    hasShippingRow,
    showDeliveryEstimateRow,
    useEstimatedTotal: figuresUsable,
    useEstimatedSubtotal: figuresUsable,
    showMovRow,
    totalIncludesDelivery: hasShippingRow || showDeliveryEstimateRow,
    useEstimatedSurcharge,
    showSurchargeRow,
    showLegacySurchargeRow,
    showTaxRow,
    useTaxInclusiveTotal,
  }
}

module.exports = {
  isPromoCodeLine,
  toAmount,
  resolveDiscountAmount,
  hasPromoSaving,
  resolveLineDiscount,
  isProductLine,
  findMinimumOrderValueAdjustmentLine,
  decideSummaryDelivery,
  hasNoProductLines,
  decideCheckoutGate,
  decideCheckoutResume,
  basketLoadSettled,
}
